import { useState, useEffect, useMemo, useCallback } from 'react';
import { Receipt, Plus, Trash2, Search, Download, ArrowUpDown, Filter, CheckCircle, Clock, Percent, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../../components/ui/Pagination';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 15;

const UNITS = [
  { v: 'forfait', l: 'forfait' }, { v: 'h', l: 'h' }, { v: 'j', l: 'j' },
  { v: 'u', l: 'u' }, { v: 'km', l: 'km' }, { v: 'lot', l: 'lot' },
];

function newItem() {
  return { name: '', description: '', quantity: 1, unit: 'forfait', unit_price: '',
           discount_label: '', discount_type: 'percent', discount_value: 0 };
}
function newDiscount() {
  return { label: '', reason: '', type: 'percent', value: 0 };
}

export default function AdminInvoices() {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    items: [newItem()],
    discounts: [],
    notes: '',
    payment_method: 'virement',
    status: 'draft',
  });
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [methodUpdating, setMethodUpdating] = useState(null);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Filters
  const [filterSearch, setFilterSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const filteredInvoices = useMemo(() => {
    let result = [...invoices];

    // Filter by search
    if (filterSearch.trim()) {
      const q = filterSearch.toLowerCase();
      result = result.filter(inv =>
        (inv.invoice_number || '').toLowerCase().includes(q) ||
        (inv.client_email || '').toLowerCase().includes(q) ||
        (inv.client_name || '').toLowerCase().includes(q)
      );
    }

    // Filter by type
    if (filterType !== 'all') {
      if (filterType === 'quote') {
        result = result.filter(inv => inv.type?.startsWith('quote'));
      } else {
        result = result.filter(inv => inv.type === filterType);
      }
    }

    // Sort by date
    result.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [invoices, filterSearch, filterType, sortOrder]);

  const paginatedInvoices = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredInvoices.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredInvoices, currentPage]);

  const totalPages = Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterSearch, filterType, sortOrder]);

  const fetchData = useCallback(async () => {
    try {
      const [invoicesRes, clientsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/invoices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/clients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      const invoicesData = await invoicesRes.json();
      const clientsData = await clientsRes.json();
      setInvoices(invoicesData);
      setClients(clientsData);
      setFilteredClients(clientsData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClientSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      setFilteredClients(clients);
    } else {
      const filtered = clients.filter(client => {
        const name = client.billing_info 
          ? `${client.billing_info.first_name} ${client.billing_info.last_name}`.toLowerCase()
          : '';
        const email = client.email.toLowerCase();
        const company = client.billing_info?.company_name?.toLowerCase() || '';
        return name.includes(query.toLowerCase()) || 
               email.includes(query.toLowerCase()) ||
               company.includes(query.toLowerCase());
      });
      setFilteredClients(filtered);
    }
    setShowClientDropdown(true);
  };

  const selectClient = (client) => {
    setFormData({ ...formData, user_id: client.id });
    const displayName = client.billing_info 
      ? `${client.billing_info.first_name} ${client.billing_info.last_name}`.trim() + ` (${client.email})`
      : client.email;
    setSearchQuery(displayName);
    setShowClientDropdown(false);
  };

  const addItem = () => {
    setFormData({ ...formData, items: [...formData.items, newItem()] });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const updateDiscount = (idx, field, value) => {
    const arr = [...formData.discounts];
    arr[idx] = { ...arr[idx], [field]: value };
    setFormData({ ...formData, discounts: arr });
  };
  const addDiscount = () => setFormData({ ...formData, discounts: [...formData.discounts, newDiscount()] });
  const removeDiscount = (idx) => setFormData({ ...formData, discounts: formData.discounts.filter((_, i) => i !== idx) });

  const totals = useMemo(() => {
    let gross = 0, lineDisc = 0;
    formData.items.forEach((it) => {
      const lg = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
      gross += lg;
      const dv = Number(it.discount_value) || 0;
      if (dv > 0) lineDisc += it.discount_type === 'percent' ? lg * (dv / 100) : dv;
    });
    lineDisc = Math.min(lineDisc, gross);
    const ht = Math.max(0, gross - lineDisc);
    let globalDisc = 0;
    formData.discounts.forEach((d) => {
      const v = Number(d.value) || 0;
      globalDisc += d.type === 'percent' ? ht * (v / 100) : v;
    });
    const after = Math.max(0, ht - globalDisc);
    return { gross, lineDisc, ht, globalDisc, after };
  }, [formData]);

  const fmtEUR = (v) => Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');

    try {
      const validItems = formData.items.filter(item => item.name && item.unit_price);
      if (validItems.length === 0) {
        throw new Error('Ajoutez au moins un article');
      }

      const itemsForApi = validItems.map((item) => ({
        name: item.name,
        description: item.description || '',
        quantity: parseFloat(item.quantity) || 1,
        unit: item.unit || 'forfait',
        unit_price: parseFloat(item.unit_price),
        discount_label: item.discount_label || '',
        discount_type: item.discount_type || 'percent',
        discount_value: Number(item.discount_value) || 0,
      }));

      const discountsForApi = formData.discounts.map((d) => ({
        label: d.label || '',
        reason: d.reason || '',
        type: d.type || 'percent',
        value: Number(d.value) || 0,
      }));

      const res = await fetch(`${API_URL}/api/admin/invoices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: formData.user_id,
          items: itemsForApi,
          discounts: discountsForApi,
          notes: formData.notes,
          status: formData.status || 'draft',
          payment_method: formData.payment_method,
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Erreur');
      }
      toast.success(formData.status === 'paid' ? 'Facture créée et marquée payée' : 'Brouillon de facture créé');
      setShowCreateForm(false);
      setFormData({
        user_id: '',
        items: [newItem()],
        discounts: [],
        notes: '',
        payment_method: 'virement',
        status: 'draft',
      });
      setSearchQuery('');
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    setDownloading(invoiceId);
    try {
      const res = await fetch(`${API_URL}/api/admin/invoices/${invoiceId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Facture_${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloading(null);
    }
  };


  const toggleStatus = async (invoice) => {
    const newStatus = invoice.status === 'paid' ? 'draft' : 'paid';
    let method = invoice.payment_method;
    if (newStatus === 'paid' && !method) {
      method = window.prompt('Méthode de paiement ? (virement / carte / espèces / autre)', 'virement');
      if (!method) return;
    }
    setStatusUpdating(invoice.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/invoices/${invoice.id}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, payment_method: method }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Erreur');
      }
      toast.success(newStatus === 'paid' ? 'Facture marquée comme payée' : 'Facture remise en brouillon');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setStatusUpdating(null);
    }
  };

  const updateInvoicePaymentMethod = async (invoice, method) => {
    if (!method || method === invoice.payment_method) return;
    setMethodUpdating(invoice.id);
    // Optimistic UI update
    setInvoices((prev) => prev.map((i) => i.id === invoice.id ? { ...i, payment_method: method } : i));
    try {
      const res = await fetch(`${API_URL}/api/admin/invoices/${invoice.id}/payment-method`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method: method }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Erreur');
      }
      toast.success('Méthode de paiement mise à jour');
    } catch (err) {
      toast.error(err.message);
      fetchData();
    } finally {
      setMethodUpdating(null);
    }
  };

  const TypeBadge = ({ type }) => {
    const badgeStyles = {
      subscription: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      quote: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      quote_deposit: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      quote_final: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      manual: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    };
    const labels = {
      subscription: 'Abonnement',
      quote: 'Devis',
      quote_deposit: 'Devis (acompte)',
      quote_final: 'Devis (solde)',
      manual: 'Manuel'
    };
    return (
      <span data-testid={`badge-${type}`} className={`px-2 py-1 rounded text-xs font-medium ${badgeStyles[type] || badgeStyles.manual}`}>
        {labels[type] || type}
      </span>
    );
  };

  if (loading) {
    return <div className="animate-pulse h-64 bg-muted rounded-xl" />;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Factures</h1>
          <p className="text-muted-foreground">{filteredInvoices.length} facture(s){filterType !== 'all' || filterSearch ? ` sur ${invoices.length}` : ''}</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="btn-primary" data-testid="new-invoice-btn">
          <Plus className="mr-2 h-4 w-4" /> Nouvelle facture
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Créer une facture manuelle</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-6">
              {error && (
                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">{error}</div>
              )}
              
              {/* Client Search */}
              <div className="space-y-2 relative">
                <label className="text-sm font-medium">Client *</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-10"
                    placeholder="Rechercher un client par nom, email ou entreprise..."
                    value={searchQuery}
                    onChange={(e) => handleClientSearch(e.target.value)}
                    onFocus={() => setShowClientDropdown(true)}
                    data-testid="create-invoice-client-search"
                  />
                </div>
                {showClientDropdown && filteredClients.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        className="px-4 py-3 hover:bg-muted cursor-pointer border-b border-border last:border-0"
                        onClick={() => selectClient(client)}
                      >
                        <p className="font-medium">
                          {client.billing_info 
                            ? `${client.billing_info.first_name} ${client.billing_info.last_name}`.trim()
                            : client.email
                          }
                        </p>
                        <p className="text-sm text-muted-foreground">{client.email}</p>
                        {client.billing_info?.company_name && (
                          <p className="text-xs text-muted-foreground">{client.billing_info.company_name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Articles</label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-1" /> Ajouter un article
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="border border-border rounded-lg p-4 bg-muted/30 space-y-3" data-testid={`item-${index}`}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs font-bold uppercase text-muted-foreground">Article {index + 1}</span>
                        {formData.items.length > 1 && (
                          <button type="button" onClick={() => removeItem(index)} className="text-destructive hover:bg-destructive/10 p-1 rounded">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <Input
                        placeholder="Désignation de l'article *"
                        value={item.name}
                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                      />
                      <textarea
                        className="w-full min-h-16 px-3 py-2 rounded-md border border-input bg-background resize-none text-sm font-mono leading-relaxed"
                        placeholder="Description détaillée (optionnel, utilisez - ou • pour des puces)"
                        value={item.description || ''}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        rows={3}
                      />
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground">Quantité</label>
                          <Input type="number" step="0.01" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Unité</label>
                          <select
                            value={item.unit || 'forfait'}
                            onChange={(e) => updateItem(index, 'unit', e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          >
                            {UNITS.map((u) => (<option key={u.v} value={u.v}>{u.l}</option>))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">PU HT (€)</label>
                          <Input type="number" step="0.01" placeholder="0.00" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', e.target.value)} />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Total HT</label>
                          <div className="h-10 flex items-center px-3 rounded-md bg-background border border-input text-sm font-semibold">
                            {(() => {
                              const lg = (Number(item.quantity)||0)*(Number(item.unit_price)||0);
                              const dv = Number(item.discount_value)||0;
                              const ld = dv > 0 ? (item.discount_type === 'percent' ? lg * (dv/100) : dv) : 0;
                              return fmtEUR(Math.max(0, lg - ld));
                            })()}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px] gap-3 pt-3 border-t border-border/50">
                        <div>
                          <label className="text-xs flex items-center gap-1.5 text-muted-foreground">
                            <Percent className="h-3 w-3" /> Remise sur cette ligne (optionnel)
                          </label>
                          <Input placeholder="Nom de la remise" value={item.discount_label || ''} onChange={(e) => updateItem(index, 'discount_label', e.target.value)} />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Type</label>
                          <select
                            value={item.discount_type || 'percent'}
                            onChange={(e) => updateItem(index, 'discount_type', e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          >
                            <option value="percent">% pourcent</option>
                            <option value="amount">€ montant</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Valeur</label>
                          <Input type="number" step="0.01" value={item.discount_value || 0} onChange={(e) => updateItem(index, 'discount_value', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Global discounts */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium flex items-center gap-2"><Percent className="h-4 w-4" /> Réductions globales (optionnel)</label>
                    <Button type="button" variant="outline" size="sm" onClick={addDiscount}>
                      <Plus className="h-4 w-4 mr-1" /> Remise
                    </Button>
                  </div>
                  {formData.discounts.map((d, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_140px_40px] gap-2 items-end">
                      <div>
                        <Label className="text-xs">Nom</Label>
                        <Input placeholder="Remise fidélité" value={d.label} onChange={(e) => updateDiscount(idx, 'label', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">Motif (optionnel)</Label>
                        <Input value={d.reason || ''} onChange={(e) => updateDiscount(idx, 'reason', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">Type</Label>
                        <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={d.type} onChange={(e) => updateDiscount(idx, 'type', e.target.value)}>
                          <option value="percent">%</option>
                          <option value="amount">€</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Valeur</Label>
                        <Input type="number" step="0.01" value={d.value} onChange={(e) => updateDiscount(idx, 'value', e.target.value)} />
                      </div>
                      <button type="button" onClick={() => removeDiscount(idx)} className="text-destructive hover:bg-destructive/10 p-2 rounded-md h-10 self-end">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium">Informations complémentaires (optionnel)</label>
                  <textarea
                    className="w-full min-h-20 mt-2 px-3 py-2 rounded-md border border-input bg-background resize-none text-sm font-mono leading-relaxed"
                    placeholder="- Acompte 30% à la signature&#10;- Solde à la livraison"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={4}
                  />
                </div>

                {/* Totals panel */}
                <div className="bg-muted/50 rounded-xl p-4 space-y-1 text-sm">
                  {totals.lineDisc > 0 && (
                    <>
                      <div className="flex justify-between"><span className="text-muted-foreground">Sous-total brut</span><span className="font-semibold">{fmtEUR(totals.gross)}</span></div>
                      <div className="flex justify-between text-red-600"><span>Remises sur lignes</span><span>− {fmtEUR(totals.lineDisc)}</span></div>
                    </>
                  )}
                  <div className="flex justify-between"><span className="text-muted-foreground">Total HT</span><span className="font-semibold">{fmtEUR(totals.ht)}</span></div>
                  {totals.globalDisc > 0 && (
                    <>
                      <div className="flex justify-between text-red-600"><span>Réduction globale</span><span>− {fmtEUR(totals.globalDisc)}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Après remise</span><span className="font-semibold">{fmtEUR(totals.after)}</span></div>
                    </>
                  )}
                  <div className="flex justify-between pt-2 mt-1 border-t border-border text-base"><span className="font-bold">Total à payer</span><span className="font-bold text-primary">{fmtEUR(totals.after)}</span></div>
                </div>
              </div>

              {/* Status + Payment Method */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Statut initial</label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="draft">Brouillon (à régler — non comptabilisée)</option>
                    <option value="paid">Payée (comptabilisée dans les revenus)</option>
                  </select>
                  <p className="text-xs text-muted-foreground">Vous pourrez la marquer payée plus tard via le bouton dans la liste.</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Méthode de paiement</label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                  >
                    <option value="virement">Virement bancaire</option>
                    <option value="especes">Espèces</option>
                    <option value="cheque">Chèque</option>
                    <option value="carte">Carte bancaire</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" disabled={creating || !formData.user_id} className="btn-primary">
                  {creating ? 'Création...' : 'Créer la facture'}
                </Button>
                <Button type="button" variant="outline" onClick={() => {
                  setShowCreateForm(false);
                  setSearchQuery('');
                  setFormData({ 
                    user_id: '', 
                    items: [{ name: '', quantity: 1, unit_price: '' }],
                    payment_method: 'virement'
                  });
                }}>
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3" data-testid="invoice-filters">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-10"
            placeholder="Rechercher par n° facture, client, email..."
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            data-testid="invoice-search"
          />
        </div>
        <select
          className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[160px]"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          data-testid="invoice-type-filter"
        >
          <option value="all">Tous les types</option>
          <option value="subscription">Abonnement</option>
          <option value="quote">Devis</option>
          <option value="manual">Manuel</option>
        </select>
        <Button
          variant="outline"
          size="sm"
          className="h-10 px-4"
          onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
          data-testid="invoice-sort"
        >
          <ArrowUpDown className="h-4 w-4 mr-2" />
          {sortOrder === 'desc' ? 'Plus récentes' : 'Plus anciennes'}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {filteredInvoices.length === 0 ? (
            <div className="p-12 text-center">
              <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {invoices.length === 0 ? 'Aucune facture' : 'Aucune facture ne correspond aux filtres'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left p-4 font-medium text-muted-foreground">N° Facture</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Client</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Type</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Méthode</th>
                    <th className="text-center p-4 font-medium text-muted-foreground">Statut</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Montant</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                      <td className="p-4 font-medium">{invoice.invoice_number}</td>
                      <td className="p-4 text-muted-foreground">{invoice.client_name || invoice.client_email}</td>
                      <td className="p-4 text-muted-foreground">
                        {new Date(invoice.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="p-4">
                        <TypeBadge type={invoice.type} />
                      </td>
                      <td className="p-4 text-muted-foreground capitalize">
                        {invoice.type === 'manual' ? (
                          <select
                            value={invoice.payment_method || ''}
                            onChange={(e) => updateInvoicePaymentMethod(invoice, e.target.value)}
                            disabled={methodUpdating === invoice.id}
                            data-testid={`method-select-${invoice.id}`}
                            className="h-8 px-2 rounded-md border border-input bg-background text-sm capitalize disabled:opacity-50"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="">— non défini —</option>
                            <option value="virement">Virement</option>
                            <option value="especes">Espèces</option>
                            <option value="cheque">Chèque</option>
                            <option value="carte">Carte</option>
                            <option value="autre">Autre</option>
                          </select>
                        ) : (
                          invoice.payment_method || '-'
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {invoice.status === 'paid' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3" /> Payée
                          </span>
                        ) : invoice.status === 'draft' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <Clock className="h-3 w-3" /> À régler
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">{invoice.status}</span>
                        )}
                      </td>
                      <td className="p-4 text-right font-semibold">{Number(invoice.amount).toFixed(2)}€</td>
                      <td className="p-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          {invoice.type === 'manual' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleStatus(invoice)}
                              disabled={statusUpdating === invoice.id}
                              data-testid={`toggle-${invoice.id}`}
                              title={invoice.status === 'paid' ? 'Remettre en brouillon' : 'Marquer comme payée'}
                            >
                              {invoice.status === 'paid' ? <Clock className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadPDF(invoice.id, invoice.invoice_number)}
                            disabled={downloading === invoice.id}
                            data-testid={`download-${invoice.id}`}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            {downloading === invoice.id ? '...' : 'PDF'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredInvoices.length}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </CardContent>
      </Card>
    </div>
  );
}
